import { createContext, useContext, useState, useEffect } from 'react';
import {
  fetchBranding,
  fetchSpaceTheme,
  applyBranding,
  applySpaceTheme,
  BRANDING_DEFAULTS,
  type PortalBranding,
} from '../lib/branding';

interface BrandingState {
  branding: PortalBranding;
  isLoading: boolean;
}

const BrandingContext = createContext<BrandingState>({
  branding: BRANDING_DEFAULTS,
  isLoading: true,
});

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<PortalBranding>(BRANDING_DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchBranding().then(async (b) => {
      setBranding(b);
      applyBranding(b);

      // If the space has its own active theme, apply it globally on top of the
      // portal branding. This covers both 'app' and 'site' space types.
      if (b.defaultSpaceId) {
        const spaceTheme = await fetchSpaceTheme(b.defaultSpaceId);
        if (spaceTheme && Object.keys(spaceTheme).length > 0) {
          applySpaceTheme(spaceTheme, b);
        }
      }

      setIsLoading(false);
    });
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, isLoading }}>{children}</BrandingContext.Provider>
  );
}

export function useBranding(): PortalBranding {
  return useContext(BrandingContext).branding;
}

export function useBrandingLoading(): boolean {
  return useContext(BrandingContext).isLoading;
}
